import React, { useState, useContext, useEffect } from "react";
import { Flex, Heading, Text, Checkbox, Select, Input, Modal, Button, MNotification, MTag } from "@cisco/spaces-dash-lib-styleguide";
import moment from "moment";
import { I18nContext } from "../../../contexts/i18nContext";
import { PropertiesContext } from "../../../contexts/propertiesContext";
import "./integration-details-tab.scss";

const useCopyToClipboard = () => {
  const [isCopied, setIsCopied] = useState(false);
  const [copyItemName, setCopyItemName] = useState(null);

  const copyToClipboard = async (content, itemName) => {
    try {
      await navigator.clipboard.writeText(content);
      setIsCopied(true);
      setCopyItemName(itemName);
      console.log("Copied to clipboard:", content);
    } catch (error) {
      setIsCopied(false);
      console.error("Unable to copy to clipboard:", error);
    }
  };

  return { isCopied, copyToClipboard, copyItemName };
};

function IntegrationDetailsTab(props) {
  const { properties } = useContext(PropertiesContext);
  const tenantId = window.sessionStorage.getItem("tenantId");
  const { isCopied, copyToClipboard, copyItemName } = useCopyToClipboard();
  const { action, type, app, setApp, awsRegions, appEndpoints, setAppEndpoints, keyRotationRenew } = props;
  const i18n = useContext(I18nContext);
  const pushChannels = { KINESIS_FIREHOSE: "AWS Kinesis Firehose", KINESIS: "AWS Kinesis", EVENT_HUBS: "Azure Event Hubs", PUB_SUB: "Google Pub/Sub" };
  const pushChannelsDesc = {
    KINESIS_FIREHOSE: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.use-channel-to-push-aws-kinesis-data-firehose-msg"),
    KINESIS: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.use-channel-to-push-aws-kinesis-data-msg"),
    EVENT_HUBS: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.use-channel-to-push-azure-events-hub-msg"),
    PUB_SUB: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.use-channel-to-push-google-sub-msg")
  };
  const pushChannelConfigDesc = {
    awsRegion: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.aws-region-text"),
    deliveryStreamName: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.delivery-stream-name-text"),
    streamName: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.stream-name-text"),
    awsAccessKey: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.aws-access-key-text"),
    awsSecretKey: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.aws-secret-key"),
    connectionString: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.connection-string-text"),
    projectId: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.project-id-text"),
    topicId: i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.topic-id-text")
  };
  const pushChannelKeys = Object.keys(pushChannels);
  const kinesisFirehose = [
    { key: "awsRegion", value: "" },
    { key: "deliveryStreamName", value: "" },
    { key: "awsAccessKey", value: "" },
    { key: "awsSecretKey", value: "" }
  ];
  const kinesis = [
    { key: "awsRegion", value: "" },
    { key: "streamName", value: "" },
    { key: "awsAccessKey", value: "" },
    { key: "awsSecretKey", value: "" }
  ];
  const eventHubs = [{ key: "connectionString", value: "" }];
  const pubSub = [
    { key: "projectId", value: "" },
    { key: "topicId", value: "" }
  ];

  const pullChannelData = [
    {
      channelName: "HTTP",
      type: "HTTP",
      channelDescription: "Pull events over a streaming HTTP Connection"
    },
    {
      channelName: "gRPC",
      type: "G_RPC",
      channelDescription: "Pull events over a streaming gRPC call"
    },
    {
      channelName: "Websocket",
      type: "WEB_SOCKET",
      channelDescription: "Pull events over a streaming Websocket Connection"
    }
  ];

  const [confirmRenewModal, setConfirmRenewModal] = useState(false);
  const [apiIndexForRenew, setApiIndexForRenew] = useState(null);
  const [rssiSettingApplicable, setRssiSettingApplicable] = useState(false);
  const [renewKeyName, setRenewKeyName] = useState("");

  const isChannelSelected = (channel) => {
    let found = false;
    if (app.endpoints && app.endpoints.length) {
      for (let j = 0, maxPoint = app.endpoints.length; j < maxPoint; j++) {
        if (app.endpoints[j].type == channel.type) {
          found = true;
        }
      }
    }
    return found;
  };

  const onClickChannel = (channel) => {
    if (isChannelSelected(channel)) {
      // remove channel
      const newEditingNode = { ...app };
      if (newEditingNode.endpoints) {
        for (let i = 0, max = newEditingNode.endpoints.length; i < max; i++) {
          if (newEditingNode.endpoints[i].type === channel.type) {
            newEditingNode.endpoints.splice(i, 1);
            break;
          }
        }
      }
      setApp(newEditingNode);
    } else {
      // push channel
      const newEditingNode = { ...app };
      if (newEditingNode.endpoints && newEditingNode.endpoints.length) {
        newEditingNode.endpoints.push({
          type: channel.type
        });
      } else {
        newEditingNode.endpoints = [];
        newEditingNode.endpoints.push({
          type: channel.type
        });
      }
      setApp(newEditingNode);
    }
  };

  const checkboxTemplate = (channel, index) => {
    return (
      <div key={index}>
        <Checkbox key={index} onClick={() => onClickChannel(channel)} checked={isChannelSelected(channel)}>
          {channel.channelName}
        </Checkbox>
        <div className="evtDesc">
          <Text>{channel.channelDescription}</Text>
        </div>
      </div>
    );
  };

  const isPushChannelSelected = (channel) => {
    let found = false;
    if (app.endpoints && app.endpoints.length) {
      for (let j = 0, maxPoint = app.endpoints.length; j < maxPoint; j++) {
        if (app.endpoints[j].type == channel) {
          found = true;
        }
      }
    }
    return found;
  };

  const getDefaultConfig = (channel) => {
    switch (channel) {
      case "KINESIS_FIREHOSE":
        return kinesisFirehose;
      case "KINESIS":
        return kinesis;
      case "EVENT_HUBS":
        return eventHubs;
      case "PUB_SUB":
        return pubSub;
      default:
        return [];
    }
  };

  const onClickPushChannel = (channel) => {
    if (isPushChannelSelected(channel)) {
      // remove channel
      const newEditingNode = { ...app };
      if (newEditingNode.endpoints) {
        for (let i = 0, max = newEditingNode.endpoints.length; i < max; i++) {
          if (newEditingNode.endpoints[i].type === channel) {
            newEditingNode.endpoints.splice(i, 1);
            break;
          }
        }
      }
      setApp(newEditingNode);
    } else {
      // push channel
      const newEditingNode = { ...app };
      if (newEditingNode.endpoints && newEditingNode.endpoints.length) {
        newEditingNode.endpoints.push({
          type: channel,
          config: getDefaultConfig(channel)
        });
      } else {
        newEditingNode.endpoints = [];
        newEditingNode.endpoints.push({
          type: channel,
          config: getDefaultConfig(channel)
        });
      }
      setApp(newEditingNode);
    }
  };

  // const handlePushChannelInput = (val) => {
  //   console.log("handlePushChannelInput:: ", val);
  // };

  const onSelectDropdown = (endpointIndex, configIndex, key, val) => {
    const newEditingAppEndpoints = [...appEndpoints];
    newEditingAppEndpoints[endpointIndex].config[configIndex][key] = val;
    setAppEndpoints(newEditingAppEndpoints);
  };

  const handleConfigChange = (endpointIndex, configIndex, key, value) => {
    const newEditingAppEndpoints = [...appEndpoints];
    newEditingAppEndpoints[endpointIndex].config[configIndex][key] = value;
    setAppEndpoints(newEditingAppEndpoints);
  };

  const appEndpointsTemplate = () => {
    return (
      <Flex className="formMaxWidth3" direction="vertical" gap={24}>
        <Flex direction="vertical" gap={16}>
          {appEndpoints.map((endpoint, endpointIndex) => {
            return pushChannelKeys.indexOf(endpoint.type) > -1 ? (
              <Flex key={endpoint.type} direction="vertical" gap={4}>
                <Checkbox key={1} onClick={() => onClickPushChannel(endpoint.type)} checked={isPushChannelSelected(endpoint.type)}>
                  {pushChannels[endpoint.type]}
                </Checkbox>
                <div className="evtDesc">
                  <Text>{pushChannelsDesc[endpoint.type]}</Text>
                </div>
                {isPushChannelSelected(endpoint.type) && endpoint.config && (
                  <Flex className="evtDesc" direction="vertical" gap={8}>
                    <Flex className="widthMax720" wrap direction="horizontal" gap={8}>
                      {endpoint.config.map((config, configIndex) => {
                        return config.key === "awsRegion" ? (
                          <div className="width340" key={config.key}>
                            <Text size="p3" weight="regular" style={{ "margin-bottom": "4px" }}>
                              {pushChannelConfigDesc[config.key]}
                            </Text>
                            <Select onChange={(val) => onSelectDropdown(endpointIndex, configIndex, "value", val)} options={awsRegions} value={config.value} />
                          </div>
                        ) : (
                          <div className="width340" key={config.key}>
                            {config.key === "connectionString" || config.key === "awsSecretKey" ? (
                              <Input.Password
                                id={`config-${endpointIndex}-${configIndex}`}
                                size="md"
                                label={pushChannelConfigDesc[config.key]}
                                value={config.value}
                                onChange={(e) => handleConfigChange(endpointIndex, configIndex, "value", e.target.value)}
                              />
                            ) : (
                              <Input
                                id={`config-${endpointIndex}-${configIndex}`}
                                size="md"
                                label={pushChannelConfigDesc[config.key]}
                                value={config.value}
                                onChange={(e) => handleConfigChange(endpointIndex, configIndex, "value", e.target.value)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </Flex>
                  </Flex>
                )}
              </Flex>
            ) : (
              ""
            );
          })}
        </Flex>
      </Flex>
    );
  };

  const renewKey = () => {
    setConfirmRenewModal(false);
    console.log("apiIndexForRenew:: ", apiIndexForRenew);

    if (app.apiKeys[apiIndexForRenew]) {
      const keyObj = {
        appId: app.appId,
        setup: app.apiKeys[apiIndexForRenew].name,
        apiKey: app.apiKeys[apiIndexForRenew].key
      };

      keyRotationRenew(keyObj);
    }
  };

  useEffect(() => {
    // read RSSI Setting enabled status for current tenant
    // properties.rssiSettingsEnabledTenants = "[11366,11310,10137,11362,11434,13063,13594,11252,11564,11222,10201,10091,14604,13121,14813,12910,14818,14824,12939,14984]";
    if (properties.rssiSettingsEnabledTenants) {
      const allowedTenants = JSON.parse(properties.rssiSettingsEnabledTenants);
      const tId = JSON.parse(tenantId);
      if (allowedTenants && allowedTenants.indexOf(tId) > -1) {
        setRssiSettingApplicable(true);
      }
    }
  }, [properties]);

  const renewConfirmationTemplate = () => {
    return (
      <Modal className="browser-back-confirm-modal" isOpen={confirmRenewModal}>
        <Flex direction="vertical">
          <Heading
            as="h2"
            margin={{
              bottom: 16
            }}
            size="primary"
          >
            {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.sure-want-to-renew-api-key-text")} {renewKeyName}
          </Heading>
          <MNotification style={{ marginBottom: 24 }} status="warning">
            {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.current-api-key-no-longer-valid-text")}
          </MNotification>
          <Flex justify="flex-end">
            <Button.Group>
              <Button
                kind="secondary"
                onClick={() => {
                  setConfirmRenewModal(false);
                }}
              >
                {i18n.t("partner-dashboard-app.auth.change-password.cancel-text")}
              </Button>
              <Button
                id="close-browser-back-modal"
                onClick={() => {
                  renewKey();
                }}
              >
                {i18n.t("partner-dashboard-app.partner-apps.app-settings.renew-text")}
              </Button>
            </Button.Group>
          </Flex>
        </Flex>
      </Modal>
    );
  };

  const showRenewConfirmPopup = (index, itemName) => {
    setConfirmRenewModal(true);
    setRenewKeyName(itemName);
    setApiIndexForRenew(index);
  };

  const getFormatDate = (date) => {
    return moment(date).format("Do MMM YYYY");
  };

  const getItemOverdue = (item) => {
    if (!item || !item.lastRotationDate) {
      return null;
    }
    const { lastRotationDate } = item;
    const dateA = moment();
    const plusIndex = lastRotationDate.indexOf("+");
    let newDateStr = lastRotationDate;
    if (plusIndex > -1) newDateStr = lastRotationDate.substring(0, plusIndex - 1);
    const dateB = moment(newDateStr);
    const dateB90 = dateB.add(90, "days");
    const remainingDays = dateB90.diff(dateA, "days") + 1;
    return !(remainingDays >= 0);
  };

  const getRemainingDays = (item) => {
    if (!item || !item.lastRotationDate) {
      return null;
    }
    const { lastRotationDate } = item;
    const dateA = moment();
    const plusIndex = lastRotationDate.indexOf("+");
    let newDateStr = lastRotationDate;
    if (plusIndex > -1) newDateStr = lastRotationDate.substring(0, plusIndex - 1);
    const dateB = moment(newDateStr);
    const dateB90 = dateB.add(90, "days");
    const remainingDays = dateB90.diff(dateA, "days") + 1;
    return remainingDays;
  };

  const getDisabledRssi = () => {
    if ((action !== "create" && app.editType !== "live") || app.region === "eu" || app.region === "sg") {
      return true;
    }
    return false;
  };

  const maskString = (str) => {
    if (str.length <= 6) return str; // Ensure string is long enough to mask

    const start = str.slice(0, 3); // First 3 characters
    const end = str.slice(-3); // Last 3 characters
    const masked = "*".repeat(str.length - 6); // Mask the middle characters

    return `${start}${masked}${end}`;
  };

  const apiKeysTemplate = () => {
    return (
      <Flex className="formMaxWidth3" direction="vertical" gap={24}>
        <Flex direction="vertical" gap={16}>
          {/* <Heading size="section">{i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.integration-types-text")}</Heading> */}
          {action !== "create" && (
            <div className="evtDesc2">
              <MTag.Status size="md" status="warning">
                {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.channels-to-power-your-apps-text")}
              </MTag.Status>
            </div>
          )}
          {action === "create" && (
            <div className="evtDesc2">
              <MTag.Status size="md" status="warning">
                {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.on-app-creation-keys-generated-text")}
              </MTag.Status>
            </div>
          )}
        </Flex>
        <div className="formDivTable">
          <div className="apiTableContainer">
            <div className="apiCredentialsTable">
              <div className="headingRow">
                <div className="apiHeadingName envTableLabel">
                  <Text>{i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.environment-text")}</Text>
                </div>
                <div className="apiHeadingName apiTableLabel">
                  <Text>{i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.api-key-text")}</Text>
                </div>
              </div>
              {action !== "create" &&
                app.apiKeys.map((item, index) => (
                  <div key={item.name} className="apiCredentialsRow">
                    {((app.stage === "LIVE" && item.name === "Production") ||
                      (app && app.stage !== "LIVE" && item.name === "Production") ||
                      (app.stage !== "LIVE" && (item.name === "Sandbox" || item.name === "Pre Production"))) && (
                      <div>
                        <div className="apiInfo envTableLabel">
                          <div className="api-setup-name">{item.name}</div>
                          <div className="api-created-date">
                            {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.created-on-text")} {getFormatDate(item.lastDate)}
                          </div>
                          <div className="api-renewal-date">
                            {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.recommended-renewal-text")} -{" "}
                            {item && item.lastRotationDate && (
                              <span>
                                {getRemainingDays(item) === 0 ? (
                                  i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.today-text")
                                ) : (
                                  <span className={getItemOverdue(item) ? "overdue-date" : ""}>
                                    {getRemainingDays(item)} {getItemOverdue(item) ? "day(s) ago" : "days"}
                                  </span>
                                )}
                              </span>
                            )}
                            {item && !item.lastRotationDate && <span>-</span>}
                          </div>
                        </div>
                        <div className="apiInfo apiTableLabel">
                          <div className="normalApiKey">
                            <Text>{item.key}</Text>
                          </div>
                          <div className="hiddenApiKey">
                            <Text>{maskString(item.key)}</Text>
                          </div>
                          <div className="copyClipboard btnTableLabel">
                            <Button style={{ "margin-right": "8px" }} onClick={() => copyToClipboard(item.key, item.name)} title="Copy Key">
                              <span className="copiedText">
                                {isCopied && copyItemName === item.name
                                  ? i18n.t("partner-dashboard-app.partner-apps.app-settings.copied-text")
                                  : i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.copy-text")}
                              </span>
                            </Button>
                            {action !== "view" && app.stage === "LIVE" && item.name === "Production" && (
                              <Button kind="secondary" onClick={() => showRenewConfirmPopup(index, item.name)} title={i18n.t("partner-dashboard-app.titles.renew-key")}>
                                {i18n.t("partner-dashboard-app.partner-apps.app-settings.renew-text")}
                              </Button>
                            )}
                            {action !== "view" && app.stage !== "LIVE" && (item.name === "Sandbox" || item.name === "Pre Production") && (
                              <Button kind="secondary" onClick={() => showRenewConfirmPopup(index, item.name)} title={i18n.t("partner-dashboard-app.titles.renew-key")}>
                                {i18n.t("partner-dashboard-app.partner-apps.app-settings.renew-text")}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </Flex>
    );
  };

  const onClickRSSI = () => {
    const newEditingNode = { ...app };
    newEditingNode.subscriptions[0].enableLocalConnection = !newEditingNode.subscriptions[0].enableLocalConnection;
    setApp(newEditingNode);
  };

  return (
    <div className="pBottom40">
      <div>
        <Flex direction="vertical" gap={16}>
          <Flex direction="vertical" gap={8}>
            <Heading size="section">{i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.integration-types-text")}</Heading>
          </Flex>
          {type === "MTC" && (
            <Flex wrap className="formMaxWidth3" direction="vertical" gap={24}>
              <Text>{i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.push-channels-text")}</Text>
              {appEndpointsTemplate()}
            </Flex>
          )}
          <Flex wrap className="formMaxWidth2" direction="vertical" gap={24}>
            <Text>{i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.pull-channels-text")}</Text>
            {pullChannelData && pullChannelData.map((channel, index) => checkboxTemplate(channel, index))}
          </Flex>
          {type === "On-Prem" && rssiSettingApplicable && (
            <Flex style={{ "margin-top": "40px" }} wrap className="formMaxWidth2" direction="vertical" gap={24}>
              <div className={getDisabledRssi() ? "disabled-section" : ""}>
                <Heading size="section">{i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.rssi-update-text")}</Heading>
                {app.region === "eu" && (
                  <MNotification style={{ marginBottom: 24 }} status="warning">
                    {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.not-applicable-for-eu-region-apps")}
                  </MNotification>
                )}
                {app.region === "sg" && (
                  <MNotification style={{ marginBottom: 24 }} status="warning">
                    {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.not-applicable-for-sg-region-apps")}
                  </MNotification>
                )}
                <Flex style={{ "margin-top": "20px" }} direction="vertical" gap={4}>
                  <Checkbox disabled={getDisabledRssi()} key="rssi-1" onClick={() => onClickRSSI()} checked={app.subscriptions[0].enableLocalConnection}>
                    {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.enable-local-rssi-text")}
                  </Checkbox>
                  <div className="evtDesc">
                    <Text>{i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.open-local-firehose-api-text")}</Text>
                  </div>
                </Flex>
              </div>
            </Flex>
          )}
          {type === "MTC" && (
            <Flex style={{ "margin-top": "40px" }} wrap className="formMaxWidth3" direction="vertical" gap={24}>
              <Heading size="section">{i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.api-credentials-for-pull-channels-text")}</Heading>
              {confirmRenewModal && renewConfirmationTemplate()}
              {apiKeysTemplate()}
            </Flex>
          )}
        </Flex>
      </div>
    </div>
  );
}

export default IntegrationDetailsTab;
